import { Grapheme, Word } from '@/common/types/scopeAndSequence';
import { CreateAttempt } from '@/common/types/session';
import { playWordAudio } from '@/common/utils/audio';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  getCumulativeGraphemes,
  getGraphemesByIds,
  getWordGraphemes,
  getWordLevel,
} from '@/common/utils/scopeAndSequence';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getGeneralGameState,
  getPhonemeForge,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { MinigameController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MinigameController';
import { CardFeedback } from '@/game/GameComponents/PhaserGame/Types/MinigameTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { calculateProgress } from '@/game/gameHelpers';
import {
  getInitialGraphemeOptions,
  playPhonemeForgeGameInstructionsAudioBatch,
} from '@/game/PhonemeForge/phonemeForgeHelpers';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Minigame from '@/game/scenes/Minigame';
import { v4 as uuidv4 } from 'uuid';

export class PhonemeForgeController extends MinigameController {
  get state() {
    return GameInstance.instance.phonemeForge;
  }

  override async startRound() {
    const gameState = getGeneralGameState();

    if (gameState.isRoundEnded) {
      GameInstance.instance.updateGeneralGameState({
        isRoundEnded: false,
      });
    }

    const wordChain = gameState.gameData.phonemeForge;
    if (!wordChain) {
      console.error('No word chain found');
      return;
    }

    let wordToCreate = getPhonemeForge().wordToCreate;
    if (!wordToCreate) {
      wordToCreate = wordChain[0];
      if (!wordToCreate) {
        console.error('No word to create found');
        return;
      }
    }

    const newBaseWordIndex = wordChain.indexOf(wordToCreate);
    const newBaseWord = wordChain[newBaseWordIndex % wordChain.length];
    const nextWordIndex = wordChain.indexOf(wordToCreate) + 1;
    const nextWord = wordChain[nextWordIndex % wordChain.length]; // Loop back if at the end

    GameInstance.instance.updatePhonemeForge({
      baseWord: newBaseWord,
      wordToCreate: nextWord,
    });

    this.setPhonemeForgeGameBoard();
  }

  override async playAudioForRound() {
    const wordChain = getGeneralGameState().gameData.phonemeForge;
    if (!wordChain) {
      console.error('No word chain found');
      return;
    }
    const baseWord = getPhonemeForge().baseWord;
    if (!baseWord) {
      console.error('No base word found');
      return;
    }
    const nextWord = getPhonemeForge().wordToCreate;
    if (!nextWord) {
      console.error('No next word found');
      return;
    }
    const wordSegmentationEnabled =
      getStudent().settings.wordSegmentationEnabled;
    const audioDelay = getStudent().settings.audio.audioDelay;
    await playPhonemeForgeGameInstructionsAudioBatch(
      baseWord,
      nextWord,
      wordSegmentationEnabled,
      audioDelay,
      this.handleSegmentationAudioPlayed.bind(this),
    );
  }

  setPhonemeForgeGameBoard() {
    const phonemeForgeState = this.state;
    const baseWord = phonemeForgeState.baseWord;
    const wordToCreate = phonemeForgeState.wordToCreate;

    if (!baseWord || !wordToCreate) {
      console.error('No base word or word to create found');
      return;
    }

    const baseWordGraphemes = getWordGraphemes(baseWord);
    const wordToCreateGraphemes = getWordGraphemes(wordToCreate);

    const level = GameInstance.instance.getShouldPlayCustomLevel()
      ? Math.max(getWordLevel(baseWord.id), getWordLevel(wordToCreate.id))
      : getStudent().level;
    const cumulativeGraphemes = getGraphemesByIds(
      getCumulativeGraphemes(level),
    );

    GameInstance.instance.updatePhonemeForge({
      studentAttempt: baseWordGraphemes.map((grapheme) => ({
        id: uuidv4(),
        grapheme,
      })),
      options: getInitialGraphemeOptions(
        baseWordGraphemes,
        wordToCreateGraphemes,
        cumulativeGraphemes,
        getStudent().settings.distractorCardsEnabled,
      ).map((grapheme) => ({
        id: uuidv4(),
        grapheme,
      })),
    });
  }

  findUniqueGraphemesInWordChain(wordChain: Word[]): Grapheme[] {
    // Use a Map to ensure unique spellings with their corresponding grapheme
    const uniqueSpellingGraphemeMap = new Map();

    wordChain.forEach((word) => {
      const wordGraphemes = getWordGraphemes(word);
      wordGraphemes.forEach((g) => {
        if (!uniqueSpellingGraphemeMap.has(g.spelling)) {
          uniqueSpellingGraphemeMap.set(g.spelling, g);
        }
      });
    });

    // Get the values from the map
    const uniqueGraphemes = Array.from(uniqueSpellingGraphemeMap.values());

    return uniqueGraphemes;
  }

  override createCardList(minigameScene: Minigame): {
    dealtCards: StudentGameCard[];
    boardCards: StudentGameCard[];
  } {
    const dealtCards = GameInstance.instance.phonemeForge.options;
    const boardCards = GameInstance.instance.phonemeForge.studentAttempt;
    const cardConfig = {
      dealtCards: dealtCards?.map((data) => {
        const newCard = new StudentGameCard(minigameScene, 0, 0);
        minigameScene.initializeNewMinigameCard(newCard, data, 'hand');
        return newCard;
      }),
      boardCards: boardCards?.map((data) => {
        const newCard = new StudentGameCard(minigameScene, 0, 0);
        minigameScene.initializeNewMinigameCard(newCard, data, 'board');
        return newCard;
      }),
    };
    this.minigame.getBoardDropSite().layoutGroup?.updateLayout('instant');
    return cardConfig;
  }

  override getMaxBoardCards(): number {
    const wordToCreate = getPhonemeForge().wordToCreate;
    const baseWord = getPhonemeForge().baseWord;
    if (wordToCreate && baseWord) {
      return Math.max(
        getWordGraphemes(wordToCreate).length,
        getWordGraphemes(baseWord).length,
      );
    } else {
      console.error('No word to create found');
      return 0;
    }
  }

  override handleMinigameCardDropped(data: {
    card: StudentGameCard;
    dropSiteName: string;
    targetIndex: number;
  }) {
    super.handleMinigameCardDropped(data);
    // Update the max cards on the board.
    const wordToCreate = getPhonemeForge().wordToCreate;
    const baseWord = getPhonemeForge().baseWord;
    if (wordToCreate && baseWord) {
      const newMaxCards = Math.max(
        getWordGraphemes(wordToCreate).length,
        this.minigame.getBoardDropSite().layoutGroup?.getCardCount() || 0,
      );
      this.minigame.getBoardDropSite().layoutGroup?.setMaxCards(newMaxCards);
    }
  }

  override async checkMinigameAnswer() {
    const currentActivityId = getSession().currentActivity?.id;
    if (!currentActivityId) {
      console.error('No current activity ID found');
      return;
    }

    const { wordToCreate } = this.state;
    if (!wordToCreate) {
      console.warn('No word to create');
      return;
    }

    const studentAttempt = this.minigame.getInput();
    if (!studentAttempt.length) {
      console.error('No student attempt');
      return;
    }

    const student = getStudent();
    const attemptData: CreateAttempt = {
      organisationId: student.organisationId,
      studentId: student.id,
      activityId: currentActivityId,
      activityName: 'phonemeforge',
      studentAnswer: studentAttempt,
      cardsPlayed: this.minigame.getBoardCards().map((card) => card.cardText),
      correctAnswer: wordToCreate.spelling,
      correctAnswerId: wordToCreate.id,
      control: this.minigame.getControlScheme(student.settings.controllerType),
      startedAt: getSession().currentAttemptStartedAt,
      endedAt: Date.now(),
    };
    GameInstance.instance.addAttempt(attemptData);
    const isCorrect = studentAttempt === wordToCreate.spelling;

    // update accuracy
    GameInstance.instance.updateActivityAccuracy(isCorrect);

    if (isCorrect) {
      // The answer was correct. Push the word to words completed.
      GameInstance.instance.updatePhonemeForge({
        wordsCompleted: [
          ...getPhonemeForge().wordsCompleted,
          wordToCreate.spelling,
        ],
      });
      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: true,
      });
      const updatedGeneralGameState = getGeneralGameState();
      const updatedPhonemeForgeState = getPhonemeForge();
      const newProgress = calculateProgress(
        updatedGeneralGameState,
        updatedPhonemeForgeState,
        100,
      );
      GameInstance.instance.updateGeneralGameState({
        progress: newProgress,
      });
      // Move to the next word in the chain
      const generalGameState = getGeneralGameState();
      const wordChain = generalGameState.gameData.phonemeForge;
      if (!wordChain) {
        console.error('No word chain found');
        return;
      }
      const newBaseWordIndex = wordChain.indexOf(wordToCreate);
      const newBaseWord = wordChain[newBaseWordIndex % wordChain.length];
      const nextWordIndex = wordChain.indexOf(wordToCreate) + 1;
      const nextWord = wordChain[nextWordIndex % wordChain.length]; // Loop back if at the end
      GameInstance.instance.updatePhonemeForge({
        baseWord: newBaseWord,
      });
      const phonemeForgeState = getPhonemeForge();
      const currentWord = phonemeForgeState.baseWord;
      if (!currentWord) {
        console.error('No current word found');
        return;
      }
      const baseWordGraphemes = getWordGraphemes(currentWord);
      const wordToCreateGraphemes = getWordGraphemes(nextWord);
      const level = GameInstance.instance.getShouldPlayCustomLevel()
        ? Math.max(getWordLevel(currentWord.id), getWordLevel(nextWord.id))
        : getStudent().level;
      const cumulativeGraphemes = getGraphemesByIds(
        getCumulativeGraphemes(level),
      );
      GameInstance.instance.updatePhonemeForge({
        studentAttempt: baseWordGraphemes.map((grapheme) => ({
          id: uuidv4(),
          grapheme,
        })),
        options: getInitialGraphemeOptions(
          baseWordGraphemes,
          wordToCreateGraphemes,
          cumulativeGraphemes,
          getStudent().settings.distractorCardsEnabled,
        ).map((grapheme) => ({
          id: uuidv4(),
          grapheme,
        })),
      });
      EventBus.emit(GAME_EVENTS.MINIGAME_CORRECT_ANSWER, attemptData);
    } else {
      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: false,
        forceIndividualFeedback: true,
        forceReadout: true,
      });
      this.setPhonemeForgeGameBoard();
      EventBus.emit(GAME_EVENTS.MINIGAME_INCORRECT_ANSWER, attemptData);
    }
  }

  override getCardFeedback(): CardFeedback[] {
    const boardState = this.minigame.getBoardDropSite().layoutGroup;
    if (!boardState) {
      console.error('No board state found');
      return [];
    }
    const wordToCreate = getPhonemeForge().wordToCreate;
    let graphemeIndex = 0;
    if (!wordToCreate) return [];
    const graphemes = getWordGraphemes(wordToCreate);

    return boardState.getMinigameCards().map((card: StudentGameCard) => {
      if (graphemeIndex >= graphemes.length) {
        return {
          card,
          isCorrect: false,
        };
      }
      const targetLetter = graphemes[graphemeIndex].spelling;

      graphemeIndex++;
      return {
        card,
        isCorrect: targetLetter === card.cardData?.grapheme?.spelling,
      };
    });
  }

  override async playInstructions() {
    const wordToCreate = this.state.wordToCreate;
    if (!wordToCreate) return;

    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Replay word button clicked', {
        word: wordToCreate.spelling,
      });
    }

    await this.soundOutWord(wordToCreate);
  }

  override canSubmitAnswer(): boolean {
    // Return true if the student has inputted something that isn't the starting word.
    const input = this.minigame.getInput();
    const baseWord = getPhonemeForge().baseWord;

    return !!input && input.length > 0 && input !== baseWord?.spelling;
  }

  override async readFinalAnswer() {
    const wordToCreate = getPhonemeForge().wordToCreate;
    if (!wordToCreate) return;
    await playWordAudio(wordToCreate.spelling);
  }
}
